import React, { useRef, useState, useEffect } from "react";
import {
    Checkbox,
    Drawer,
    FormControlLabel,
    FormGroup,
    IconButton,
    Stack,
    TextField,
    Snackbar,
    AlertProps,
    Alert,
    Tooltip,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button, Typography,
    List, ListItem, Link, Box
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import readNDJSONStream from "ndjson-readablestream";
import styles from "./Chat.module.css";

import {
    chatApi,
    configApi,
    RetrievalMode,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    ResponseMessage,
    VectorFieldOptions,
    GPT4VInput,
    getSourceDocuments,
    getChatSession,
    getSimilarQuestions,
    SimilarQuestion,
    SimilarQuestionOrError
} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { DomainListAnimated } from '../../components/DomainOption';
import { AgentSelector } from "../../components/AgentSelector";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { SettingsButton } from "../../components/SettingsButton";
import { ClearChatButton } from "../../components/ClearChatButton";
import { SwitchDomainButton } from "../../components/SwitchDomainButton";
import { useLogin, getToken, isLoggedIn, requireAccessControl, getActiveAccount, hasDeveloperRole, isEcrAgentUser } from "../../authConfig";
import { VectorSettings } from "../../components/VectorSettings";
import { useMsal } from "@azure/msal-react";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";
import { GPT4VSettings } from "../../components/GPT4VSettings";
import Footer from "../../components/Footer/Footer";
import { CHATBOT_WEBSITE_NAME, RAG_AGENT, ECR_AGENT } from "../../constants";
import GpscAuraLogo from "../../components/GpscAuraLogo/GpscAuraLogo";
import {DomainOption as DomainOptionList } from "../../api/models";

const Chat = () => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [retrieveCount, setRetrieveCount] = useState<number>(5);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
    const [vectorFieldList, setVectorFieldList] = useState<VectorFieldOptions[]>([VectorFieldOptions.Embedding]);
    const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(false);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
    const [gpt4vInput, setGPT4VInput] = useState<GPT4VInput>(GPT4VInput.TextAndImages);
    const [useGPT4V, setUseGPT4V] = useState<boolean>(false);
    const [selectedDomain, setSelectedDomain] = useState<string>("");
    const [selectedAgents, setSelectedAgents] = useState<string[]>([ECR_AGENT]);
    const [snackbar, setSnackbar] = useState<Pick<AlertProps, "children" | "severity"> | null>(null);
    const [canSwitchDomain, setCanSwitchDomain] = useState<boolean>(true);
    const [domainsList, setDomainsList] = useState<DomainOptionList[]>([]);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const questionBoxSpace = useRef<HTMLDivElement | null>(null);
    const bottomOfChatStream = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [showGPT4VOptions, setShowGPT4VOptions] = useState<boolean>(false);

    const [sourceDocuments, setSourceDocuments] = useState<string[]>([]);
    const [chatSession, setChatSession] = useState<string>("");
    const [showSettingsButton, setShowSettingsButton] = useState<boolean>(false);

    const IS_HYBRID_APPROACH = true;

    const [ragRetirementPopup, setRagRetirementPopup] = useState<boolean>(false);

    const ragRetirementNotice = () => {
        // Check if the notice has already been shown in this session
        const hasShownNotice = sessionStorage.getItem('ragRetirementNoticeShown');
        if (!hasShownNotice) {
            setRagRetirementPopup(true);
        }
    };

    const handleCloseRagRetirementPopup = () => {
        // Mark that the notice has been shown in this session
        sessionStorage.setItem('ragRetirementNoticeShown', 'true');
        setRagRetirementPopup(false);
    };

    const getConfig = async () => {
        const token = client ? await getToken(client) : undefined;

        configApi(token).then(config => {
            setShowGPT4VOptions(config.showGPT4VOptions);
        });
    };

    const getSources = async () => {
        const token = client ? await getToken(client) : undefined;

        const response = await getSourceDocuments(token);
        if (response.ok) {
            const sources = (await response.json()) as string[];
            setSourceDocuments(sources);
        } else {
            console.error(`Error fetching source documents: ${response.statusText}`);
        }
    };

    const getNewChatSession = async () => {
        const token = client ? await getToken(client) : undefined;

        const response = await getChatSession(token);
        if (response.ok) {
            const data = await response.json();
            const chat_session_id = data["chat_session_id"];
            setChatSession(chat_session_id);
        } else {
            console.error(`Error starting new session: ${response.statusText}`);
        }
    };

    const handleAgentsChange = (agents: string[]) => {
        localStorage.setItem('selectedAgents', JSON.stringify(agents));
        setSelectedAgents(agents);
    };

    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], setAnswers: Function, responseBody: ReadableStream<any>) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {} as ChatAppResponse;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...askResponse,
                        choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
                    };
                    setStreamedAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (event["choices"] && event["choices"][0]["context"] && event["choices"][0]["context"]["data_points"]) {
                    event["choices"][0]["message"] = event["choices"][0]["delta"];
                    askResponse = event as ChatAppResponse;
                } else if (event["choices"] && event["choices"][0]["delta"]["content"]) {
                    setIsLoading(false);
                    await updateState(event["choices"][0]["delta"]["content"]);
                } else if (event["choices"] && event["choices"][0]["context"]) {
                    // Update context with new keys from latest event
                    askResponse.choices[0].context = { ...askResponse.choices[0].context, ...event["choices"][0]["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                } else if (event["chat_id"]) {
                    askResponse.chat_id = event["chat_id"];
                }
            }
        } finally {
            setIsStreaming(false);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
        };
        return fullResponse;
    };

    const client = useLogin ? useMsal().instance : undefined;
    const id_claims = client ? JSON.stringify(getActiveAccount(client)?.idTokenClaims ?? {}) : undefined;

    const convertToAnswerObject = (verified_questions: SimilarQuestion, question: string) => {
        const response: ChatAppResponse = {
            choices: [
                {
                    message: {
                        content: "We have searched our database for GM Verified Questions and found the following similar questions:",
                        role: "assistant"
                    },
                    index: 0,
                    session_state: null,
                    context: {
                        data_points: { text: [] },
                        thoughts: [],
                        followup_questions: null
                    },
                    original_question: question,
                    gm_verified: true,
                    verified_questions: verified_questions.verified_questions
                }
            ],
            chat_id: ""
        };
        setAnswers([...answers, [question, response]]);
    };

    const makeApiRequest = async (question: string, hybrid: boolean) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        const token = client ? await getToken(client) : undefined;

        try {
            if (hybrid) {
                const response = await getSimilarQuestions(token, question, selectedDomain);
                const parsedResponse: SimilarQuestionOrError = await response.json();
                if (response.status > 299 || !response.ok) {
                    throw Error(parsedResponse.error || "Unknown error");
                }

                const similarQuestions = parsedResponse as SimilarQuestion;
                similarQuestions.verified_questions = similarQuestions.verified_questions.filter(verifiedQuestion => verifiedQuestion.similarity_score >= 0.7);

                if (similarQuestions.verified_questions.length > 0) {
                    convertToAnswerObject(similarQuestions, question);
                    return;
                }
            }

            const messages: ResponseMessage[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].choices[0].message.content, role: "assistant" }
            ]);

            const idClaims = activeAccount?.idTokenClaims as Record<string, unknown> | undefined;
            const userId = idClaims && typeof idClaims.uid === "string" ? idClaims.uid : "";

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                stream: shouldStream,
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        top: retrieveCount,
                        retrieval_mode: retrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        vector_fields: vectorFieldList,
                        use_gpt4v: useGPT4V,
                        gpt4v_input: gpt4vInput,
                        selected_domain: selectedDomain,
                        enabled_servers: selectedAgents,
                        user_id: userId
                    },
                    chat_session_id: chatSession
                },
                // ChatAppProtocol: Client must pass on any session state received from the server. Test test test please work
                session_state: answers.length ? answers[answers.length - 1][1].choices[0].session_state : null
            };

            const response = await chatApi(request, token);
            if (!response.body) {
                throw Error("No response body");
            }
            if (shouldStream) {
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(question, answers, setAnswers, response.body);
                setAnswers([...answers, [question, parsedResponse]]);
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (response.status > 299 || !response.ok) {
                    throw Error(parsedResponse.error || "Unknown error");
                }
                setAnswers([...answers, [question, parsedResponse as ChatAppResponse]]);
            }
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = async () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
        await getNewChatSession();
    };

    const setDomain = (domain: string) => {
        localStorage.setItem('selectedDomain', domain);
        setSelectedDomain(domain);
    };

    const resetDomainAndClearChat = () => {
        localStorage.removeItem('selectedDomain');
        setSelectedDomain("");
        clearChat();
    };


    useEffect(() => {
        if (isLoading) {
            // Only scroll when loading begins, not when loading ends
            chatMessageStreamEnd.current?.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "nearest"
            });
            //When question is asked, scroll outer view to show end of chat + question box below
            questionBoxSpace.current?.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "nearest"
            });
        }
    }, [isLoading]);

    useEffect(() => {
      // no longer allowing UI user to select domain - web requests exclusively use NO_DOMAIN_SELECTED
      // ignoring cached frontend info

        //const storedDomain = localStorage.getItem('selectedDomain');
        // if (storedDomain) {
        //     setSelectedDomain(storedDomain);
        // }
      setSelectedDomain("NO_DOMAIN_SELECTED");

        // const storedAgents = localStorage.getItem('selectedAgents');
        // let agents: string[] = [];
        // if (storedAgents) {
        //     try {
        //         const parsedAgents = JSON.parse(storedAgents);
        //         if (Array.isArray(parsedAgents)) {
        //             agents = parsedAgents;
        //         }
        //     } catch (e) {
        //         console.error("Error parsing selected agents from local storage", e);
        //     }
        // }

        // SITSRS-4619: Force all users to ECR agent only, filter out any cached RAG preferences
        setSelectedAgents([ECR_AGENT]);
    }, []);

    /*
    *  While the answer is streaming, continuously repeat the isLoading behavior
    *  so the answer remains visible and the answer box CSS grows in the right direction
    */
    useEffect(() => {
        if (!isLoading && streamedAnswers.length > 0) {
            // For streaming updates, prioritize the message view
            chatMessageStreamEnd.current?.scrollIntoView({
                behavior: "auto",
                block: "end",
                inline: "nearest"
            });
            questionBoxSpace.current?.scrollIntoView({
                behavior: "auto",
                block: "end",
                inline: "nearest"
            });
        }
    }, [streamedAnswers]);

    useEffect(() => {
        ragRetirementNotice();
        getConfig();
        getSources();
    }, []);
    useEffect(() => {
        getNewChatSession();
        setShowSettingsButton(useLogin ? hasDeveloperRole(activeAccount) : true);
    }, [isLoggedIn(client)]);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "3"));
    };

    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };

    const onShouldStreamChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setShouldStream(!!checked);
    };

    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };

    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const onUseOidSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseOidSecurityFilter(!!checked);
    };

    const onUseGroupsSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseGroupsSecurityFilter(!!checked);
    };

    const onExampleClicked = (example: string) => {
        makeApiRequest(example, IS_HYBRID_APPROACH);
    };
    const onDomainOptionClicked = (domain: string) => {
        setTimeout(() => { setDomain(domain); }, 700);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const activeAccount = useLogin ? getActiveAccount(client) : undefined;
    const hasEcrAccess = isEcrAgentUser(activeAccount);

    const handleCloseSnackbar = () => setSnackbar(null);

    return (
        <div className={styles.container}>
            <div className={styles.commandsContainer}>
                {/* SITSRS-4619: Only show domain section when RAG agent is active */}
                {selectedAgents.includes(RAG_AGENT) && <span className={styles.commandsSubcontainer}>
                    <div className={styles.commandButton} style={{ marginTop: "1px" }}>
                        <span> Document Q&A Domain: </span>
                        <span style={{ paddingRight: "3px" }}>
                            {selectedDomain && <b>{selectedDomain}</b>}
                            {!selectedDomain && <i>None selected</i>}
                        </span>
                    </div>
                    {canSwitchDomain && (
                        <SwitchDomainButton className={styles.commandButton} onClick={resetDomainAndClearChat} disabled={isLoading} />
                    )}
                    {!canSwitchDomain && (
                        <Tooltip title={"Only one domain available, cannot switch"}>
                            <span style={{ display: 'inline-block' }}>
                                <SwitchDomainButton className={styles.commandButton} onClick={() => void 0} disabled={true} />
                            </span>
                        </Tooltip>
                    )}
                </span>}
                {/* SITSRS-4619: Agent selector temporarily disabled - preserve for future re-enablement
                {
                    isEcrAgentUser(getActiveAccount(client)) && (
                        <span>
                            <AgentSelector
                                selectedAgents={selectedAgents}
                                onAgentsChange={handleAgentsChange}
                                disabled={isLoading}
                            />
                        </span>
                    )
                }
                */}
                <span className={styles.commandsSubcontainer} style={{ marginRight: "15px" }}>
                    <Button
                        variant="contained"
                        size="small"
                        className={styles.commandButton}
                        onClick={() => setRagRetirementPopup(true)}
                    >
                        Notice About Document Q&A Shutdown
                    </Button>
                </span>
                <span className={styles.commandsSubcontainer}>
                    <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={!lastQuestionRef.current || isLoading} />
                </span>
                <span className={styles.commandsSubcontainer}>
                    {showSettingsButton && <SettingsButton className={styles.commandButton} onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)} />}
                </span>
            </div>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!lastQuestionRef.current ? (
                        <div className={styles.chatEmptyState}>
                            <GpscAuraLogo className={styles.chatEmptyStateLogo} />
                            {/* SITSRS-4619: Domain selection only shown when RAG agent is active */}
                            {!selectedDomain && selectedAgents.includes(RAG_AGENT) && (
                                <div>
                                    <h2 className={styles.domainSelectionHeader}>Please select a domain:</h2>
                                    <DomainListAnimated onDomainOptionClicked={onDomainOptionClicked} setSnackbar={setSnackbar} onDomainsLoaded={setCanSwitchDomain} domainsList={setDomainsList}/>
                                </div>
                            )}
                            {/* SITSRS-4619: Show "Ask me anything!" for ECR-only users immediately */}
                            <div hidden={selectedAgents.includes(RAG_AGENT) && !selectedDomain}>
                                <h2 className={styles.chatEmptyStateSubtitle}>Your ECR Assistant</h2>
                                {/*<ExampleList onExampleClicked={onExampleClicked} useGPT4V={useGPT4V} />*/}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {isStreaming &&
                                streamedAnswers.map((streamedAnswer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={streamedAnswer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={true}
                                                key={index}
                                                answer={streamedAnswer[1]}
                                                isSelected={false}
                                                sources={sourceDocuments}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q, false)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                domainsList={domainsList}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {!isStreaming &&
                                answers.map((answer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={answer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={false}
                                                key={index}
                                                answer={answer[1]}
                                                isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                sources={sourceDocuments}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q, false)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                domainsList={domainsList}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {isLoading && (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}
                            {error ? (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current, false)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}
                    <span ref={bottomOfChatStream}> </span>

                    {/* SITSRS-4619: Show input when domain selected OR RAG not active (ECR only) */}
                    {(selectedDomain || !selectedAgents.includes(RAG_AGENT)) && (
                        <Container className={styles.chatInput}>
                            <QuestionInput
                                clearOnSend
                                placeholder={
                                    !hasEcrAccess
                                        ? `You don't appear to have access to any current ${CHATBOT_WEBSITE_NAME} Agents. For Document Q&A, please use Glean.`
                                        : "Type a new question"
                                }
                                disabled={!hasEcrAccess || isLoading || isStreaming || (selectedAgents.includes(RAG_AGENT) && !selectedDomain) || selectedAgents.length === 0}
                                showDisabledStyle={!hasEcrAccess}
                                onSend={question => makeApiRequest(question, IS_HYBRID_APPROACH)}
                            />
                        </Container>
                    )}
                </div>

                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                    />
                )}

                <Drawer anchor="right" open={isConfigPanelOpen}>
                    <Container>
                        <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
                            <h2>Configure answer generation</h2>
                            <div>
                                <IconButton onClick={() => setIsConfigPanelOpen(false)}>
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        </Stack>
                        <Stack spacing={4}>
                            <FormGroup>
                                <TextField
                                    margin="normal"
                                    defaultValue={promptTemplate}
                                    multiline
                                    onChange={onPromptTemplateChange}
                                    label="Override prompt template"
                                />
                                <TextField
                                    margin="normal"
                                    type="number"
                                    defaultValue={retrieveCount.toString()}
                                    onChange={onRetrieveCountChange}
                                    label="Retrieve this many search results:"
                                />
                                <TextField margin="normal" onChange={onExcludeCategoryChanged} label="Exclude category" />
                                <FormControlLabel
                                    control={<Checkbox checked={useSemanticRanker} onChange={onUseSemanticRankerChange} />}
                                    label="Use semantic ranker for retrieval"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={useSemanticCaptions} onChange={onUseSemanticCaptionsChange} disabled={!useSemanticRanker} />}
                                    label="Use query-contextual summaries instead of whole documents"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={useSuggestFollowupQuestions} onChange={onUseSuggestFollowupQuestionsChange} />}
                                    label="Suggest follow-up questions"
                                />
                                {showGPT4VOptions && (
                                    <GPT4VSettings
                                        gpt4vInputs={gpt4vInput}
                                        isUseGPT4V={useGPT4V}
                                        updateUseGPT4V={setUseGPT4V}
                                        updateGPT4VInputs={setGPT4VInput}
                                    />
                                )}
                                <VectorSettings
                                    showImageOptions={useGPT4V && showGPT4VOptions}
                                    updateVectorFields={setVectorFieldList}
                                    updateRetrievalMode={setRetrievalMode}
                                />
                                {useLogin && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={useOidSecurityFilter || requireAccessControl}
                                                disabled={!isLoggedIn(client) || requireAccessControl}
                                                onChange={onUseOidSecurityFilterChange}
                                            />
                                        }
                                        label="Use oid security filter"
                                    />
                                )}
                                {useLogin && (
                                    <FormControlLabel
                                        className={styles.chatSettingsSeparator}
                                        control={
                                            <Checkbox
                                                checked={useGroupsSecurityFilter || requireAccessControl}
                                                disabled={!isLoggedIn(client) || requireAccessControl}
                                                onChange={onUseGroupsSecurityFilterChange}
                                            />
                                        }
                                        label="Use groups security filter"
                                    />
                                )}
                                <FormControlLabel
                                    className={styles.chatSettingsSeparator}
                                    control={<Checkbox checked={shouldStream} onChange={onShouldStreamChange} />}
                                    label="Stream chat completion responses"
                                />
                            </FormGroup>
                            {useLogin && <TokenClaimsDisplay />}
                        </Stack>
                    </Container>
                </Drawer>
            </div>
            <span className={styles.answerLoadBottomLocation} ref={questionBoxSpace}></span>
            <Footer />
            {!!snackbar && (
                <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "center" }} onClose={handleCloseSnackbar} autoHideDuration={6000}>
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
            <Dialog
                open={ragRetirementPopup}
                onClose={handleCloseRagRetirementPopup}
                aria-labelledby="rag-retirement-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <Box sx={{ pl: 2 }}>
                    <DialogTitle id="rag-retirement-dialog-title">
                        We’ve moved Document Q&A to Glean
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" >Document Q&A is no longer supported in {CHATBOT_WEBSITE_NAME}.</Typography>
                        <Typography variant="body1" >You can now access this capability in Glean using the links below:</Typography>
                        <List sx={{ listStyleType: 'disc', pl: 4, py: 0 }}>
                          <ListItem sx={{ display: 'list-item', py: 0, minHeight: 'auto' }}>
                              <Link
                                  href="https://generalmotors.glean.com/chat/agents/eacda23eb2294032a639729774a1ca69?qe=https%3A%2F%2Fgeneral-motors-be.glean.com"
                                  target="_blank" rel="noopener noreferrer"
                              >
                                  Purchasing
                              </Link>
                          </ListItem>
                          <ListItem sx={{ display: 'list-item', py: 0, minHeight: 'auto' }}>
                              <Link
                                href="https://generalmotors.glean.com/chat/agents/e896109c93e441efb5ee867681309751?qe=https%3A%2F%2Fgeneral-motors-be.glean.com"
                                target="_blank" rel="noopener noreferrer"
                              >
                                  Supplier Quality
                              </Link>
                          </ListItem>
                          <ListItem sx={{ display: 'list-item', py: 0, minHeight: 'auto' }}>
                            <Link
                                href="https://generalmotors.glean.com/chat/agents/e7ad39243d4f447ea8b4f16441c6308a?qe=https%3A%2F%2Fgeneral-motors-be.glean.com"
                                target="_blank" rel="noopener noreferrer"
                            >
                                PPCO
                            </Link>
                          </ListItem>
                        </List>

                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={handleCloseRagRetirementPopup} color="primary" autoFocus>
                            I understand
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </div>
    );
};

export default Chat;
